import React, { useEffect, useState, useRef } from 'react';

const KirdReport = ({ filteredData, schoolData, fyear, kirdtype, onClose }) => {
  // State declarations
  const [openingBalanceforFirstPage, setOpeningBalanceforFirstPage] = useState(0);
  const [carryForwardBalances, setCarryForwardBalances] = useState([]);
  const language = localStorage.getItem("language") || "English";
  const udiseNumber = localStorage.getItem("udiseNumber");
  const [accountData, setAccountData] = useState({});
  const [studentData, setStudentData] = useState({});
  const [combinedStudentFees, setCombinedStudentFees] = useState({});
  const [attachedCredit, setAttachedCredit] = useState('');
  const printRef = useRef();

  // Fetch account data to get lgtype information
  const fetchAccountData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/account/kird/${kirdtype}/${udiseNumber}/${fyear}.json`);
      const data = await response.json();
      setAccountData(data);
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };

  // Fetch student data
  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/studentData.json`);
        const data = await response.json();
        setStudentData(data);
      } catch (error) {
        console.error("Error fetching student data:", error);
      }
    };
    fetchStudentData();
  }, [udiseNumber]);

  // Calculate totals
  const calculateTotalCredit = (date) => {
    return filteredData[date]?.jama?.reduce((total, item) => {
      if (item.khatePrakar === 'ByCash' && item.pavtiNo !== "0") {
        return total + parseFloat(item.amount);
      }
      return total;
    }, 0) || 0;
  };

  const calculateTotalDebit = (date) => {
    return filteredData[date]?.nave?.reduce((total, item) => {
      if (item.khatePrakar === 'ByCash') {
        return total + parseFloat(item.amount);
      }
      return total;
    }, 0) || 0;
  };

  // Find opening balance
  const findOpeningBalance = () => {
    for (const date in filteredData) {
      const { jama } = filteredData[date];
      const foundEntry = jama.find(item => item.credit1 === "31-03-Akher shillak");
      if (foundEntry) {
        setOpeningBalanceforFirstPage(parseFloat(foundEntry.amount));
        break;
      }
    }
  };

  // Calculate carry forward balances
  useEffect(() => {
    findOpeningBalance();
    fetchAccountData();

    const newCarryForwardBalances = [];
    let previousBalance = openingBalanceforFirstPage;

    const sortedDates = getAllDates();
    sortedDates.forEach((date) => {
      const totalCredit = calculateTotalCredit(date);
      const totalDebit = calculateTotalDebit(date);
      const totalPaid = combinedStudentFees[date]?.totalPaid || 0;
      const lastBalance = previousBalance + totalCredit + totalPaid - totalDebit;
      newCarryForwardBalances.push(lastBalance);
      previousBalance = lastBalance;
    });

    setCarryForwardBalances(newCarryForwardBalances);
  }, [filteredData, openingBalanceforFirstPage, combinedStudentFees]);

  const getOpeningBalance = (index) => {
    if (index === 0) return openingBalanceforFirstPage || 0;
    return carryForwardBalances[index - 1] || 0;
  };

  // Get kird with fee attach data
  const getKirdWithFeeattachData = () => {
    return new Promise((resolve, reject) => {
      const openRequest = indexedDB.open('SchoolManagementDB', 1);
      openRequest.onerror = (event) => reject(event.target.error);
      openRequest.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['kirdWithFeeattach'], 'readonly');
        const objectStore = transaction.objectStore('kirdWithFeeattach');
        const getRequest = objectStore.get(fyear);
        getRequest.onerror = (event) => reject(event.target.error);
        getRequest.onsuccess = (event) => resolve(event.target.result);
      };
    });
  };

  // Fetch combined student fees
  const fetchCombinedStudentFees = async () => {
    try {
      const kirdData = await getKirdWithFeeattachData();
      if (kirdData && kirdData.typeofattach === "combinedPavati") {
        setAttachedCredit(kirdData.creditKhate);
        const studentFeesUrl = `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/studentFee/${fyear}.json`;
        const response = await fetch(studentFeesUrl);
        const studentFees = await response.json();
        
        const feeGroups = {};
        const lastBeforePayMap = {};

        for (const serialNo in studentFees) {
          for (const invoiceNo in studentFees[serialNo]) {
            const feeData = studentFees[serialNo][invoiceNo];
            
            for (const feeType in feeData) {
              const feeDetails = feeData[feeType];
              const date = feeDetails.date;
              
              if (!feeGroups[date]) {
                feeGroups[date] = {
                  totalPaid: 0,
                  invoiceNumbers: new Set(),
                  transactions: []
                };
              }

              const previousTotal = lastBeforePayMap[`${serialNo}_${feeType}`] || 0;
              const currentBeforePay = parseFloat(feeDetails.beforePay) || 0;
              const paidAmount = currentBeforePay - previousTotal;

              if (paidAmount > 0) {
                feeGroups[date].invoiceNumbers.add(invoiceNo);
                feeGroups[date].totalPaid += parseFloat(paidAmount);
                feeGroups[date].transactions.push({
                  invoiceNo,
                  feeType,
                  paidAmount: paidAmount.toFixed(2),
                  date,
                });
                lastBeforePayMap[`${serialNo}_${feeType}`] = currentBeforePay;
              }
            }
          }
        }

        setCombinedStudentFees(feeGroups);
      }
    } catch (error) {
      console.error("Error fetching student fees:", error);
    }
  };

  useEffect(() => {
    fetchCombinedStudentFees();
  }, [filteredData]);

  // Create a combined list of dates that have either account data or student fee data
  const getAllDates = () => {
    const accountDates = Object.keys(filteredData || {});
    const feeDates = Object.keys(combinedStudentFees || {});
    const allDates = new Set([...accountDates, ...feeDates]);
    return Array.from(allDates).sort((a, b) => new Date(a) - new Date(b));
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString); 
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=600,width=1000');
    printWindow.document.write('<html><head><title>Day Book</title>');
    printWindow.document.write(`
      <style>
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th, td {
          padding: 8px;
          text-align: center;
          border: 1px solid black;
        }
        thead {
          background-color: #6495ED;
          color: white;
        }
        tfoot {
          font-weight: bold;
          background-color: #f9f9f9;
        }
        .receipt-header {
          text-align: center;
          margin-bottom: 20px;
        }
      </style>
    `);
    printWindow.document.write('</head><body>');
    printWindow.document.write(printRef.current.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div style={reportOverlay}>
      <div style={reportContainer}>
        <button style={closeButtonStyle} onClick={onClose}>Close</button>
        <button onClick={handlePrint} style={printButtonStyle}>
          Print Report
        </button>
  
        <div ref={printRef} style={{ overflowY: 'auto', maxHeight: '80vh' }}>
          {getAllDates().map((date, index) => {
            // Check if we have account data for this date
            const hasAccountData = filteredData && filteredData[date];
            
            // Calculate totals - use 0 if no account data
            const totalCredit = hasAccountData ? calculateTotalCredit(date) : 0;
            const totalDebit = hasAccountData ? calculateTotalDebit(date) : 0;
            const openingBalance = getOpeningBalance(index);
            const totalPaid = combinedStudentFees[date]?.totalPaid || 0;
            
            // Calculate totals for display
            let totalCashCredit = 0;
            let totalBankCredit = 0;
            let totalCashDebit = 0;
            let totalBankDebit = 0;

            if (hasAccountData) {
              filteredData[date].jama.forEach(item => {
                if (item.khatePrakar === 'ByCash') {
                  totalCashCredit += parseFloat(item.amount);
                } else {
                  totalBankCredit += parseFloat(item.amount);
                }
              });

              filteredData[date].nave.forEach(item => {
                if (item.khatePrakar === 'ByCash') {
                  totalCashDebit += parseFloat(item.amount);
                } else {
                  totalBankDebit += parseFloat(item.amount);
                }
              });
            }

            const totalCreditAmount = totalCashCredit + totalBankCredit;
            const totalDebitAmount = totalCashDebit + totalBankDebit;
            const lastBalance = (openingBalance + totalCreditAmount + totalPaid - totalDebitAmount).toFixed(2);
            const closingBalance = (openingBalance + totalCreditAmount + totalPaid - totalDebitAmount).toFixed(2);

            return (
              <div key={date} style={{ width: '210mm', height: '297mm', border: '1px solid black', margin: '20px auto', padding: '10mm', boxSizing: 'border-box' }}>
                {/* School Info Section */}
                <div style={{ backgroundColor: "#f0f0f0", padding: "20px", marginBottom: "20px", textAlign: "center", border: "1px solid black #000" }}>
                  <h1 style={{ margin: "0", fontSize: "24px" }}>{schoolData.schoolName}</h1>
                  <p style={{ margin: "5px 0", fontSize: "16px" }}>
                    Taluka: {schoolData.taluka}, District: {schoolData.jilha}
                  </p>
                  <p style={{ margin: "5px 0", fontSize: "16px" }}>
                    Email: {schoolData.email}, Phone: {schoolData.mobileNumber}
                  </p>
                  <h6 style={{ textAlign: "center", backgroundColor: "#000", color: "#fff", padding: "4px", display: "block", width: "fit-content", margin: "0 auto", borderRadius: "10px", marginBottom: "10px", fontSize: "20px", marginTop:"20px" }}>
                    {language === "English" ? "Day Book " : "किर्दवही "}
                  </h6>
                </div>

                <h6><b>{language === "English" ? "Date " : "दिनांक "}</b>{new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' })}</h6>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', marginBottom: '20px' }}>
                  <thead>
                    <tr>
                      <th colSpan={4} style={{ textAlign: 'center', backgroundColor: '#6495ED', border: '1px solid #000', color: 'white' }}>
                        <b>{language === "English" ? "Credit " : "जमा "}</b>
                      </th>
                      <th colSpan={4} style={{ textAlign: 'center', backgroundColor: '#6495ED', border: '1px solid #000', color: 'white' }}>
                        <b>{language === "English" ? "Debit " : "नावे "}</b>
                      </th>
                    </tr>
                    <tr>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Invoice No " : "पावती नंबर "}</b>
                      </th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Account name and note " : "खात्याचे नाव व टीप "}</b>
                      </th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Cash amount " : "रोख रक्कम "}</b>
                      </th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Bank Amount " : "बँक रक्कम"}</b>
                      </th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Voucher No " : "व्हाउचर नंबर"}</b>
                      </th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Account name and note " : "खात्याचे नाव व टीप "}</b>
                      </th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Cash Amount " : "रोख रक्कम "}</b>
                      </th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#ccc' }}>
                        <b>{language === "English" ? "Bank Amount " : "बँक रक्कम"}</b>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {/* Student Fee Row - Always show if there's data */}
                    {totalPaid > 0 && (
                      <tr key={`student-fee-${date}`}>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                          {attachedCredit} <br />
                          <small style={{color:'#836459'}}>transferred from students Fee</small> <br />
                          <small> ({language==="English"?"Invoice No:- ": "पावती क्र:- "}   {Array.from(combinedStudentFees[date].invoiceNumbers).join(', ')})</small>
                        </td>
                        {accountData?.jama?.khate && Object.values(accountData.jama.khate).find(k => k.khate === attachedCredit)?.lgtype === 'normal' ? (
                          <>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {totalPaid.toFixed(2)}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                          </>
                        ) : (
                          <>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {totalPaid.toFixed(2)}
                            </td>
                          </>
                        )}
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                      </tr>
                    )}

                    {/* Regular Kird Entries - Only show if account data exists */}
                    {hasAccountData && Array.from({ length: Math.max(filteredData[date].jama.length, filteredData[date].nave.length) }).map((_, idx) => {
                      const creditEntry = filteredData[date].jama[idx];
                      const debitEntry = filteredData[date].nave[idx];

                      if (creditEntry && creditEntry.pavtiNo != 0) {
                        return (
                          <tr key={`row-${idx}`}>
                            {/* Credit Side */}
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {creditEntry.pavtiNo || ''}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {creditEntry.credit1 || ''}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {creditEntry.khatePrakar === 'ByCash' ? creditEntry.amount || '' : ''}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {creditEntry.khatePrakar !== 'ByCash' ? creditEntry.amount || '' : ''}
                            </td>

                            {/* Debit Side */}
                            {debitEntry ? (
                              <>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                  {debitEntry.pavtiNo || ''}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                  {debitEntry.debit1 || ''}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                  {debitEntry.khatePrakar === 'ByCash' ? debitEntry.amount || '' : ''}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                  {debitEntry.khatePrakar !== 'ByCash' ? debitEntry.amount || '' : ''}
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                              </>
                            )}
                          </tr>
                        );
                      } else if (debitEntry) {
                        return (
                          <tr key={`row-${idx}`}>
                            <td colSpan="4" style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}></td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {debitEntry.pavtiNo || ''}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {debitEntry.debit1 || ''}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {debitEntry.khatePrakar === 'ByCash' ? debitEntry.amount || '' : ''}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                              {debitEntry.khatePrakar !== 'ByCash' ? debitEntry.amount || '' : ''}
                            </td>
                          </tr>
                        );
                      }
                      return null;
                    }).filter(row => row !== null)}
                  </tbody>

                  <tfoot>
                    <tr>
                      <th colSpan="3" style={{ textAlign: 'center', border: "1px solid #000" }}>
                        <b>{language === "English" ? "Opening Balance" : "आरंभिक शिल्लक"}</b>
                      </th>
                      <td style={{ textAlign: 'center', border: '1px solid #000' }}>{parseFloat(openingBalance).toFixed(2) || '0'}</td>
                      <th colSpan="3" style={{ textAlign: 'center', border: "1px solid #000" }}>
                        <b>{language === "English" ? "Total Debit" : "एकूण नावे"}</b>
                      </th>
                      <td style={{ textAlign: 'center', border: '1px solid #000' }}>{totalDebitAmount.toFixed(2)}</td>
                    </tr>

                    <tr>
                      <th colSpan="3" style={{ textAlign: 'center', border: "1px solid #000" }}>
                        <b>{language === "English" ? "Total Accumulation" : "एकूण जमा"}</b>
                      </th>
                      <td style={{ textAlign: 'center', border: '1px solid #000' }}>
                        {(
                          (hasAccountData ? 
                            filteredData[date].jama
                              .filter(entry => entry.pavtiNo !== '0')
                              .reduce((acc, entry) => acc + (parseFloat(entry.amount) || 0), 0) : 0) + 
                          totalPaid
                        ).toFixed(2)}
                      </td>
                      <th colSpan="3" style={{ textAlign: 'center', border: "1px solid #000" }}>
                        <b>{language === "English" ? "Closing Balance" : "अखेर शिल्लक"}</b>
                      </th>
                   <td style={{ textAlign: 'center', border: '1px solid #000' }}>
  {(
    (hasAccountData && filteredData[date].jama.some(entry => entry.pavtiNo === '0')
      ? (totalCreditAmount + totalPaid - totalDebitAmount)  // exclude openingBalance
      : (openingBalance + totalCreditAmount + totalPaid - totalDebitAmount) // include it
    )
  ).toFixed(2)}
</td>

                    </tr>
                    <tr>
                      <th colSpan="3" style={{ textAlign: 'center', border: "1px solid #000" }}>
                        <b>{language === "English" ? "Total Amount" : "एकूण"}</b>
                      </th>
                      <td style={{ textAlign: 'center', border: '1px solid #000' }}>
                        {(
                          openingBalance + 
                          (hasAccountData ? 
                            filteredData[date].jama
                              .filter(entry => entry.pavtiNo !== '0')
                              .reduce((acc, entry) => acc + (parseFloat(entry.amount) || 0), 0) : 0) +
                          totalPaid
                        ).toFixed(2)}
                      </td>

                      <th colSpan="3" style={{ textAlign: 'center', border: "1px solid #000" }}>
                        <b>{language === "English" ? "Total " : "एकूण"}</b>
                      </th>

                      <td style={{ textAlign: 'center', border: '1px solid #000' }}>
                        {(
                          (openingBalance + 
                            (hasAccountData ? 
                              filteredData[date].jama
                                .filter(entry => entry.pavtiNo !== '0')
                                .reduce((acc, entry) => acc + (parseFloat(entry.amount) || 0), 0) : 0) +
                            totalPaid
                          ) - totalDebitAmount + 
                          totalDebitAmount
                        ).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Styles
const reportOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backdropFilter: 'blur(3px)',
  zIndex: 1000,
  marginTop: '20px'
};

const reportContainer = {
  background: 'white',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  maxWidth: '900px',
  width: '100%',
  position: 'relative',
  color: 'black',
  overflowY: 'hidden'
};

const closeButtonStyle = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  background: 'transparent',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  color: 'Red'
};

const printButtonStyle = {
  margin: '10px 0',
  padding: '10px 20px',
  backgroundColor: '#007bff',
  color: '#fff',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
};

export default KirdReport;